import type { AdditionalTraveller } from '@elara/domain/models/schemas/TravelerProfileSchema';

import type { TravellerListControl } from '#ui/TravellerList.js';

import { useRef } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { useCurrentUserProfile, useRouterState, useUserProfileState } from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { Button, Loader } from '#ui/index.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';
import { SavedPreferenceList } from '#ui/SavedPreferenceList.js';
import { TravellerList } from '#ui/TravellerList.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

/**
 * Travel companions. Each companion saves on its own (Save on its card), and a saved companion's
 * preferences save as they're changed — there's no page-level Save.
 */
export function FamilyPage() {
  const profile = useCurrentUserProfile();
  const profileState = useUserProfileState();
  const routerState = useRouterState();
  const listRef = useRef<TravellerListControl>(null);

  if (!profile) {
    return (
      <AppLayout
        backButtonRoute={ROUTES.ACCOUNT}
        title={<AppLayoutTitle title="Family & Friends" />}
        transitionKey={routerState.currentRoute?.path}
      >
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  // Preferences come from the live profile by companion id, so they reflect what's stored — including
  // facts Elara remembers while the page is open.
  const storedPreferences = (id: string) => profile.travellers?.find((t) => t.id === id)?.preferences ?? [];

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Family & Friends" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs
                items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Family & Friends' }]}
                className="mb-3"
              />
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-3 flex-1">
                  <h1 className="text-2xl xl:text-3xl font-display font-light">Family & Friends</h1>
                  <p className="text-muted-foreground">Manage your travel companions.</p>
                </div>
                <Button variant="default" onClick={() => listRef.current?.addTraveller()}>
                  <MaterialIcon icon="person_add" /> Add a traveller
                </Button>
              </div>
            </div>

            <TravellerList
              initial={profile.travellers ? [...(profile.travellers as AdditionalTraveller[])] : []}
              controlRef={listRef}
              onSave={async (traveller) => profileState.saveTraveller(traveller)}
              onRemove={async (traveller) => profileState.removeTraveller(traveller.id)}
              renderPreferences={(traveller) => (
                <SavedPreferenceList
                  label="Preferences"
                  placeholder="Add a preference"
                  entries={storedPreferences(traveller.id)}
                  onAdd={async (value) =>
                    profileState.addPreference({ value, category: 'general', travellerId: traveller.id })
                  }
                  onUpdate={async (entry, value) => profileState.updatePreference(entry.id, value)}
                  onRemove={async (entry) => profileState.removePreference(entry)}
                />
              )}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
